import React, { useState, useMemo } from 'react';
import { useAuth } from '../../context/AuthContext';
import apiService from '../../services/api';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const { FiMessageSquare, FiAlertCircle, FiCheck, FiSend, FiZap } = FiIcons;

const Feedback = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('feature'); // 'feature', 'bug', 'recommendation'
  const [title, setTitle] = useState(''); // Only for recommendation
  const [shortDescription, setShortDescription] = useState(''); // Only for recommendation
  const [content, setContent] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success', 'error', or null
  const [errorMessage, setErrorMessage] = useState('');

  const modules = useMemo(() => ({
    toolbar: {
      container: [
        [{ 'header': [1, 2, 3, false] }],
        ['bold', 'italic', 'underline', 'strike', 'blockquote'],
        [{ 'color': [] }, { 'background': [] }],
        [{ 'list': 'ordered'}, { 'list': 'bullet' }, { 'indent': '-1'}, { 'indent': '+1' }],
        [{ 'align': [] }],
        ['link', 'image'],
        ['clean']
      ],
      handlers: {
        image: function() {
          const range = this.quill.getSelection();
          const value = prompt('Bitte geben Sie die Bild-URL ein:');
          if(value) {
            this.quill.insertEmbed(range.index, 'image', value, 'user');
          }
        }
      }
    }
  }), []);

  const formats = [
    'header',
    'bold', 'italic', 'underline', 'strike', 'blockquote',
    'color', 'background',
    'list', 'bullet', 'indent',
    'align',
    'link', 'image'
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (activeTab === 'recommendation') {
        const isContentEmpty = !content || content === '<p><br></p>';
        if (!title.trim() || !shortDescription.trim() || isContentEmpty) return;
    } else {
        if (!content.trim()) return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);
    setErrorMessage('');

    try {
      if (activeTab === 'feature') {
        await apiService.createFeatureRequest({
          user: parseInt(user.id),
          request: content
        });
      } else if (activeTab === 'bug') {
        await apiService.createBugReport({
          user: parseInt(user.id),
          bug_report: content
        });
      } else if (activeTab === 'recommendation') {
        await apiService.createRecommendation({
          title: title,
          description: content,
          short_description: shortDescription,
          is_active: 0 // Default to inactive
        });
      }

      setSubmitStatus('success');
      setContent('');
      setTitle('');
      setShortDescription('');
      
      setTimeout(() => {
        setSubmitStatus(null);
      }, 3000);
    } catch (error) {
      console.error('Error submitting feedback:', error);
      setSubmitStatus('error');
      setErrorMessage('Ein Fehler ist aufgetreten. Bitte versuchen Sie es später erneut.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSubmitStatus(null);
    setContent('');
    setTitle('');
    setShortDescription('');
    setErrorMessage('');
  };

  const isSubmitDisabled = () => {
      if (isSubmitting) return true;
      if (activeTab === 'recommendation') {
          const isContentEmpty = !content || content === '<p><br></p>';
          return !title.trim() || !shortDescription.trim() || isContentEmpty;
      }
      return !content.trim();
  };

  const isRec = activeTab === 'recommendation';

  // Styles to make ReactQuill fill the flex container
  const quillStyles = `
    .quill { display: flex; flex-direction: column; height: 100%; }
    .ql-container { flex: 1; display: flex; flex-direction: column; overflow: hidden; font-family: inherit; font-size: inherit; }
    .ql-editor { flex: 1; overflow-y: auto; }
  `;

  return (
    <div className={`mx-auto max-w-5xl p-4 ${isRec ? 'h-[calc(100vh-80px)] flex flex-col overflow-hidden' : 'min-h-screen'}`}>
      <div className="mb-4 flex-none">
        <h1 className="text-2xl font-bold text-brand-dark flex items-center">
          <SafeIcon icon={FiMessageSquare} className="w-7 h-7 mr-3 text-brand-secondary" />
          Feedback & Vorschläge
        </h1>
        <p className="text-gray-600 mt-1 ml-10 text-sm">
          Helfen Sie uns, die Plattform zu verbessern. Senden Sie uns Feature-Wünsche, Fehlermeldungen oder schlagen Sie neue Handlungsempfehlungen vor.
        </p>
      </div>

      <div className={`bg-white rounded-xl shadow-sm border border-gray-200 ${isRec ? 'flex-1 flex flex-col overflow-hidden' : ''}`}>
        {/* Tabs */}
        <div className="flex border-b border-gray-200 overflow-x-auto flex-none">
          <button
            onClick={() => handleTabChange('feature')}
            className={`flex-1 py-3 px-6 text-sm font-medium flex items-center justify-center transition-colors whitespace-nowrap ${
              activeTab === 'feature'
                ? 'bg-white text-brand-secondary border-b-2 border-brand-secondary'
                : 'bg-gray-50 text-gray-500 hover:text-gray-700 hover:bg-gray-100'
            }`}
          >
            <SafeIcon icon={FiMessageSquare} className="w-4 h-4 mr-2" />
            Feature Request
          </button>
          <button
            onClick={() => handleTabChange('bug')}
            className={`flex-1 py-3 px-6 text-sm font-medium flex items-center justify-center transition-colors whitespace-nowrap ${
              activeTab === 'bug'
                ? 'bg-white text-brand-secondary border-b-2 border-brand-secondary'
                : 'bg-gray-50 text-gray-500 hover:text-gray-700 hover:bg-gray-100'
            }`}
          >
            <SafeIcon icon={FiAlertCircle} className="w-4 h-4 mr-2" />
            Bug Report
          </button>
          <button
            onClick={() => handleTabChange('recommendation')}
            className={`flex-1 py-3 px-6 text-sm font-medium flex items-center justify-center transition-colors whitespace-nowrap ${
              activeTab === 'recommendation'
                ? 'bg-white text-brand-secondary border-b-2 border-brand-secondary'
                : 'bg-gray-50 text-gray-500 hover:text-gray-700 hover:bg-gray-100'
            }`}
          >
            <SafeIcon icon={FiZap} className="w-4 h-4 mr-2" />
            Handlungsempfehlung
          </button>
        </div>

        <div className={`p-6 ${isRec ? 'flex-1 flex flex-col min-h-0 overflow-hidden' : ''}`}>
          <form onSubmit={handleSubmit} className={isRec ? 'flex flex-col h-full' : ''}>
            
            {activeTab === 'recommendation' && (
              <div className="flex-none">
                <div className="mb-3">
                  <label 
                    htmlFor="rec-title" 
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Titel der Empfehlung
                  </label>
                  <input
                    id="rec-title"
                    type="text"
                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-brand-primary focus:ring-brand-primary sm:text-sm p-2 border"
                    placeholder="Kurzer, prägnanter Titel"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    required
                  />
                </div>
                <div className="mb-3">
                  <label 
                    htmlFor="rec-short-desc" 
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Kurzbeschreibung
                  </label>
                  <textarea
                    id="rec-short-desc"
                    rows="2"
                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-brand-primary focus:ring-brand-primary sm:text-sm p-2 border resize-none"
                    placeholder="Eine kurze Zusammenfassung..."
                    value={shortDescription}
                    onChange={(e) => setShortDescription(e.target.value)}
                    required
                  />
                </div>
              </div>
            )}

            <div className={`mb-4 ${isRec ? 'flex-1 flex flex-col min-h-0' : ''}`}>
              <label 
                htmlFor="feedback-content" 
                className="block text-sm font-medium text-gray-700 mb-2 flex-none"
              >
                {activeTab === 'feature' 
                  ? 'Welches Feature würden Sie sich wünschen?' 
                  : activeTab === 'bug'
                  ? 'Beschreiben Sie den Fehler so genau wie möglich:'
                  : 'Detaillierte Beschreibung (HTML):'}
              </label>

              {activeTab === 'recommendation' ? (
                 <div className="flex-1 flex flex-col min-h-0 relative">
                    <style>{quillStyles}</style>
                    <ReactQuill 
                        theme="snow" 
                        value={content} 
                        onChange={setContent}
                        modules={modules}
                        formats={formats}
                        className="h-full flex flex-col"
                        placeholder="Ausführliche Beschreibung der Handlungsempfehlung..."
                    />
                 </div>
              ) : (
                <textarea
                    id="feedback-content"
                    rows="6"
                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-brand-primary focus:ring-brand-primary sm:text-sm p-3 border h-[50vh] resize-y"
                    placeholder={activeTab === 'feature' 
                    ? 'Beschreiben Sie Ihre Idee...' 
                    : 'Was haben Sie gemacht? Was ist passiert? Was hätten Sie erwartet?'}
                    value={content}
                    onChange={(e) => setContent(e.target.value)}
                    required
                />
              )}
              
              {activeTab === 'recommendation' && (
                <p className="text-xs text-gray-500 mt-2 flex-none">
                  Hinweis: Neue Handlungsempfehlungen werden zunächst geprüft und müssen von einem Administrator freigeschaltet werden.
                </p>
              )}
            </div>

            <div className={`flex items-center justify-between ${isRec ? 'flex-none mt-auto pt-2 border-t border-gray-100' : ''}`}>
                <div className="flex-1 mr-4">
                    {submitStatus === 'success' && (
                    <div className="p-2 bg-green-50 text-green-700 rounded-md flex items-center text-sm">
                        <SafeIcon icon={FiCheck} className="w-4 h-4 mr-2" />
                        Erfolgreich übermittelt!
                    </div>
                    )}

                    {submitStatus === 'error' && (
                    <div className="p-2 bg-red-50 text-red-700 rounded-md flex items-center text-sm">
                        <SafeIcon icon={FiAlertCircle} className="w-4 h-4 mr-2" />
                        {errorMessage}
                    </div>
                    )}
                </div>

              <button
                type="submit"
                disabled={isSubmitDisabled()}
                className={`flex-none flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-brand-primary hover:bg-brand-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-primary ${
                  isSubmitDisabled() ? 'opacity-60 cursor-not-allowed' : ''
                }`}
              >
                {isSubmitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Wird gesendet...
                  </>
                ) : (
                  <>
                    <SafeIcon icon={FiSend} className="w-4 h-4 mr-2" />
                    Absenden
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Feedback;